import { useCallback, useEffect, useRef } from 'react';

const SWIPE_THRESHOLD = 50;
const TAP_THRESHOLD = 10;

export function useDungeonInput({
  isMobile,

  isStatusOpen,
  isMerchantOpen,
  isShrineOpen,
  isWellOpen,
  isStorylineActive,
  isCombatActive,
  isPreCombatTransitionActive,
  trapQTE,
  pendingDeckCullReward,
  activeLore,
  barredDoorLore,
  isRansackedChestOpen,
  isWellEchoOpen,
  isSimpleChestResourceOpen,

  handleMove,
  handleTap,
  handleTileClick,
}) {
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });

  const isUiTarget = useCallback((target) => {
    return !!target?.closest?.('[data-ui="true"]');
  }, []);

  const getTileCoordsFromTarget = useCallback((target) => {
    const el = target?.closest?.('[data-tile="true"]');
    if (!el) return null;

    const x = Number(el.getAttribute('data-x'));
    const y = Number(el.getAttribute('data-y'));

    if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
    return { x, y };
  }, []);

  const blockMapTouchInput =
    isStatusOpen ||
    isMerchantOpen ||
    isShrineOpen ||
    isWellOpen ||
    isStorylineActive ||
    isCombatActive ||
    isPreCombatTransitionActive ||
    !!trapQTE ||
    !!pendingDeckCullReward ||
    !!activeLore ||
    !!barredDoorLore ||
    isRansackedChestOpen ||
    isWellEchoOpen ||
    isSimpleChestResourceOpen;

  useEffect(() => {
    const handleKey = (e) => {
      const tag = e.target?.tagName;
      const isTypingTarget =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        e.target?.isContentEditable;

      if (isTypingTarget) return;

      // Support both Arrow keys and WASD.
// Prefer `code` so it still works with Shift/Caps or different keyboard locales.
const key = e.key;
const code = e.code;

const up = key === 'ArrowUp' || key === 'w' || key === 'W' || code === 'KeyW';
const down = key === 'ArrowDown' || key === 's' || key === 'S' || code === 'KeyS';
const left = key === 'ArrowLeft' || key === 'a' || key === 'A' || code === 'KeyA';
const right = key === 'ArrowRight' || key === 'd' || key === 'D' || code === 'KeyD';

if (up) {
  e.preventDefault();
  handleMove(0, -1);
} else if (down) {
  e.preventDefault();
  handleMove(0, 1);
} else if (left) {
  e.preventDefault();
  handleMove(-1, 0);
} else if (right) {
  e.preventDefault();
  handleMove(1, 0);
}
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleMove]);

  const handleTouchStart = useCallback(
    (e) => {
      if (isUiTarget(e.target)) return;
      if (blockMapTouchInput) return;

      const touch = e.touches?.[0];
      if (!touch) return;

      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      touchEndRef.current = { x: touch.clientX, y: touch.clientY };
    },
    [blockMapTouchInput, isUiTarget]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (isUiTarget(e.target)) return;
      if (blockMapTouchInput) return;

      const touch = e.touches?.[0];
      if (!touch) return;

      touchEndRef.current = { x: touch.clientX, y: touch.clientY };
    },
    [blockMapTouchInput, isUiTarget]
  );

  const handleTouchEnd = useCallback(
    (e) => {
      if (isUiTarget(e.target)) return;
      if (blockMapTouchInput) return;

      const { x: startX, y: startY } = touchStartRef.current;
      const { x: endX, y: endY } = touchEndRef.current;

      let dx = endX - startX;
      let dy = endY - startY;

      if (isMobile) {
        dx = -dx;
        dy = -dy;
      }

      if (Math.abs(dx) < TAP_THRESHOLD && Math.abs(dy) < TAP_THRESHOLD) {
        const coords = getTileCoordsFromTarget(e.target);

        if (coords) {
          handleTileClick(coords.x, coords.y);
        } else {
          handleTap();
        }

        return;
      }

      if (Math.abs(dx) > Math.abs(dy)) {
        if (Math.abs(dx) > SWIPE_THRESHOLD) {
          if (dx > 0) handleMove(1, 0);
          else handleMove(-1, 0);
        }
      } else {
        if (Math.abs(dy) > SWIPE_THRESHOLD) {
          if (dy > 0) handleMove(0, 1);
          else handleMove(0, -1);
        }
      }
    },
    [
      blockMapTouchInput,
      getTileCoordsFromTarget,
      handleMove,
      handleTap,
      handleTileClick,
      isMobile,
      isUiTarget,
    ]
  );

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
}